<template>
    <div class="metrics-configuration-container">
        <ToastsContainer />
        <SidebarComponent />
        <div class="metrics-configuration-content">
            <div class="title-row">
                <h1>Metrics Configuration</h1>
                <ViewInformation />
                <div>
                    <div class="callout">
                        <p>Once you finish configuring metrics, hit Save Configuration in order for your changes to apply across the system.</p>
                    </div>
                </div>
                <SaveButton v-if="acl('edit_configure_metrics')" />
            </div>
            <div class="workstream-metrics-row">
                <WorkstreamMetrics />
            </div>
            <div>
                <div
                    v-for="category in state.categories"
                    :key="category.headerName"
                >
                    <CategorySection
                        v-bind="category"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import {
    CategorySection, SaveButton, ToastsContainer, ViewInformation, WorkstreamMetrics
} from '@/components';
import { MetricsConfigColumnDefinition } from '@/types/MetricsConfigColumnDefinition';
import { PerformanceMetricMetadata } from '@/types/PerformanceMetricMetadata';
import { applySavedMetricsConfiguration } from '@/utils/applySavedMetricsConfiguration';
import { state } from '@/utils/state';
import { useACLs } from '@agyletime/vue-3-common/src/composables/acl';
import _ from 'lodash';
import { onMounted } from 'vue';
import { getAllMetricDefinitions } from './getMetricDefinitions';
import { getSavedMetricsConfig } from './getSavedMetricsConfig';

const { acl } = useACLs();

function columnDefsToGroupedMetricsConfig(definitions: Array<PerformanceMetricMetadata>): Array<MetricsConfigColumnDefinition> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, complexity
    const performanceMetricsMetadata: MetricsConfigColumnDefinition[] = definitions.reduce((metadata: any, definition) => {
        const { category, subcategory = '', defaultName, displayName, dataType, id, isEnabled } = definition;
        const subcategoryHasColumns = metadata[category]?.children[subcategory]?.children.length > 0;

        const newColumn: MetricsConfigColumnDefinition = {
            // If there's no defaultName, then the displayName is the default
            headerName: defaultName || displayName,
            // If there's both a defaultName and a displayName, then the latter is the custom label.
            label: defaultName ? displayName : '',
            dataType: dataType,
            field: id,
            isSelected: isEnabled || false,
            children: [],
            parent: {
                headerName: subcategory,
                field: '',
                dataType: '',
                parent: {
                    headerName: category, field: '', dataType: ''
                }
            }
        };

        return {
            ...metadata,
            [category]: {
                headerName: category,
                children: {
                    ...(metadata[category]?.children || []),
                    [subcategory]: {
                        headerName: subcategory,
                        field: id.split('_')[1],
                        children: subcategoryHasColumns ? [ ...metadata[category].children[subcategory].children, newColumn ] : [newColumn],
                        parent: { headerName: category }
                    }
                }
            }
        };
    }, {});

    // Convert the children back to an array
    Object.values(performanceMetricsMetadata).forEach((category) => {
        category.children = Object.values(category.children);
    });

    return Object.values(performanceMetricsMetadata);
}

function convertColumnDefinitionsToMetricsConfig(
    columnDefinitions: Array<PerformanceMetricMetadata>,
    savedMetricsConfig: Array<MetricsConfigColumnDefinition>
): Array<MetricsConfigColumnDefinition> {
    // Iterate through saved config to apply isEnabled field to definitions
    const columnsWithSavedConfigApplied = applySavedMetricsConfiguration(columnDefinitions, savedMetricsConfig);
    // Group the columns by category, subcategory
    return columnDefsToGroupedMetricsConfig(columnsWithSavedConfigApplied);
}

onMounted(async () => {
    const columnDefinitions = await getAllMetricDefinitions();
    const savedMetricsConfig = await getSavedMetricsConfig();
    const columns = convertColumnDefinitionsToMetricsConfig(columnDefinitions, savedMetricsConfig);
    state.categories = _.cloneDeep(columns);
    state.savedCategories = _.cloneDeep(savedMetricsConfig);
});
</script>
<style lang="scss">
@import './style.scss';
</style>
